/**
 * SpeechEngine - Uses OpenAI TTS API instead of Web Speech API
 * Completely outside React component lifecycle to avoid race conditions
 */
import api from '../api';

class SpeechEngine {
  constructor() {
    this.queue = [];
    this.isSpeaking = false;
    this.audioContext = null;
    this.currentAudio = null;
    this.voice = 'nova'; // OpenAI TTS voice: nova (bright, energetic - perfect for AURA)
    this.onStartListeners = [];
    this.onEndListeners = [];
    
    // Initialize audio context (needed for playing audio)
    this.initAudioContext();
  }

  initAudioContext() {
    try {
      // Create audio context for playing audio
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.log('[SpeechEngine] Audio context initialized');
    } catch (error) {
      console.error('[SpeechEngine] Failed to initialize audio context:', error);
    }
  }

  addOnStartListener(listener) {
    if (typeof listener === 'function') {
      this.onStartListeners.push(listener);
    }
  }

  addOnEndListener(listener) {
    if (typeof listener === 'function') {
      this.onEndListeners.push(listener);
    }
  }

  removeOnStartListener(listener) {
    this.onStartListeners = this.onStartListeners.filter((l) => l !== listener);
  }

  removeOnEndListener(listener) {
    this.onEndListeners = this.onEndListeners.filter((l) => l !== listener);
  }

  notifyStart(text) {
    this.onStartListeners.forEach((listener) => {
      try {
        listener(text);
      } catch (e) {
        console.error('[SpeechEngine] onStart listener error', e);
      }
    });
  }

  notifyEnd(text) {
    this.onEndListeners.forEach((listener) => {
      try {
        listener(text);
      } catch (e) {
        console.error('[SpeechEngine] onEnd listener error', e);
      }
    });
  }

  async speak(text) {
    if (!text || typeof text !== 'string' || text.trim() === '') {
      return;
    }

    console.log(`[SpeechEngine] Adding to queue: "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`);
    this.queue.push(text);
    this.processQueue();
  }

  processQueue = async () => {
    if (this.isSpeaking || this.queue.length === 0) {
      if (this.isSpeaking) {
        console.log('[SpeechEngine] Already speaking, will process queue when current speech ends');
      }
      return;
    }

    this.isSpeaking = true;
    const text = this.queue.shift();

    try {
      console.log(`[SpeechEngine] Fetching TTS audio for: "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`);
      
      // Call OpenAI TTS API via backend
      const formData = new FormData();
      formData.append('text', text.trim());
      formData.append('voice', this.voice);

      // Optimize TTS request for faster response
      const response = await api.post('/api/v1/robot/speak', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob', // Important: receive audio as blob
        timeout: 60000, // 60 second timeout (TTS can take 5-15 seconds, but allow more time for slow connections)
        // Note: Response streaming is handled by axios automatically for blob responses
      });

      // Create audio element and play
      const audioBlob = response.data;
      const audioUrl = URL.createObjectURL(audioBlob);
      
      const audio = new Audio(audioUrl);
      this.currentAudio = audio;

      audio.onplay = () => {
        console.log(`[SpeechEngine] Started speaking: "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`);
        this.notifyStart(text);
      };

      audio.onended = () => {
        console.log(`[SpeechEngine] Finished speaking: "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"`);
        // Clean up
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        this.isSpeaking = false;
        this.notifyEnd(text);
        // Process next item in queue after a short delay
        setTimeout(this.processQueue, 100);
      };

      audio.onerror = (event) => {
        console.error('[SpeechEngine] Audio playback error:', event);
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        this.isSpeaking = false;
        this.notifyEnd(text);
        // Try to process next item in queue even on error
        setTimeout(this.processQueue, 100);
      };

      // Play the audio
      await audio.play();
      
    } catch (error) {
      console.error('[SpeechEngine] Failed to generate or play speech:', error);
      this.isSpeaking = false;
      // Try to process next item in queue even on error
      setTimeout(this.processQueue, 100);
    }
  };

  cancel() {
    console.log('[SpeechEngine] Cancelling all speech and clearing queue');
    this.queue = []; // Clear the queue
    
    // Stop current audio if playing
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    
    this.isSpeaking = false;
  }

  clearQueue() {
    console.log('[SpeechEngine] Clearing queue');
    this.queue = [];
  }

  setVoice(voice) {
    const validVoices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
    if (validVoices.includes(voice)) {
      this.voice = voice;
      console.log(`[SpeechEngine] Voice changed to: ${voice}`);
    } else {
      console.warn(`[SpeechEngine] Invalid voice: ${voice}. Using default: nova`);
      this.voice = 'nova';
    }
  }
}

// Create and export a SINGLETON instance of the engine
export const speechEngine = new SpeechEngine();
